/// <amd-module name="Repos/Discussion/Models/DiscussionCommentModel" />
import { IDiscussionComment, IDiscussionCommentModel } from "Repos/Discussion/Common/DiscussionContracts";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableValue
} from "VSS/Core/Observable";
import { equals as stringEquals } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";

export interface IDiscussionCommentModelProps {
    comment: IDiscussionComment;
    onDirtyChange?: () => void;
}

export class DiscussionCommentModel implements IDiscussionCommentModel {
    private props: IDiscussionCommentModelProps;
    private author: IdentityRef;
    private id: number;
    private originalComment: IDiscussionComment;

    private content: IObservableValue<string>;
    private contentExists: IObservableValue<boolean>;
    private deleted: IObservableValue<boolean>;
    private dirty: IObservableValue<boolean>;
    private editing: IObservableValue<boolean>;
    private error: IObservableValue<Error | null>;
    private likes: IObservableArray<IdentityRef>;
    private saving: IObservableValue<boolean>;
    private unread: IObservableValue<boolean>;

    constructor(props: IDiscussionCommentModelProps) {
        this.props = props;

        const { comment } = this.props;
        this.author = comment.author;
        this.id = comment.id;

        comment.content = comment.content || "";
        comment.usersLiked = comment.usersLiked || [];
        this.originalComment = comment;

        const content = this.originalComment.content || "";
        this.content = new ObservableValue(content);
        this.contentExists = new ObservableValue(content.trim().length > 0);
        this.deleted = new ObservableValue(comment.isDeleted);
        this.dirty = new ObservableValue(false);
        this.editing = new ObservableValue(false);
        this.error = new ObservableValue(null);
        this.likes = new ObservableArray(comment.usersLiked);
        this.saving = new ObservableValue(false);
        this.unread = new ObservableValue(false);
    }

    public delete(): void {
        this.deleted.value = true;
        this.originalComment.isDeleted = true;
    }

    public getAuthor(): IdentityRef {
        return this.author;
    }

    public getContent(): IReadonlyObservableValue<string> {
        return this.content;
    }

    public getError(): IReadonlyObservableValue<Error | null> {
        return this.error;
    }

    public getId(): number {
        return this.id;
    }

    public getLastContentUpdatedDate(): Date | undefined {
        return this.originalComment.lastContentUpdatedDate;
    }

    public getLastUpdatedDate(): Date | undefined {
        return this.originalComment.lastUpdatedDate;
    }

    public getLikes(): IReadonlyObservableArray<IdentityRef> {
        return this.likes;
    }

    public getPublishedDate(): Date | undefined {
        return this.originalComment.publishedDate;
    }

    public hasContent(): IReadonlyObservableValue<boolean> {
        return this.contentExists;
    }

    public isDeleted(): IReadonlyObservableValue<boolean> {
        return this.deleted;
    }

    public isDirty(): IReadonlyObservableValue<boolean> {
        return this.dirty;
    }

    public isEditing(): IReadonlyObservableValue<boolean> {
        return this.editing;
    }

    public isSaved(): boolean {
        return this.id > 0;
    }

    public isSaving(): IReadonlyObservableValue<boolean> {
        return this.saving;
    }

    public isOwner(identity: IdentityRef): boolean {
        const author = this.getAuthor();
        return author && stringEquals(author.id, identity.id, true);
    }

    public isUnread(): IReadonlyObservableValue<boolean> {
        return this.unread;
    }

    public liked(identity: IdentityRef): boolean {
        return this.getLikeIndex(identity) >= 0;
    }

    public revertComment(): void {
        this.setContent(this.originalComment.content || "");
        this.editing.value = false;
        this.error.value = null;
    }

    public setContent(content: string): void {
        this.content.value = content;

        const contentExists = content.trim().length > 0;
        if (this.contentExists.value !== contentExists) {
            this.contentExists.value = contentExists;
        }

        this.checkDirty();
    }

    public setError(error: Error): void {
        this.error.value = error;
    }

    public setUnread(author: IdentityRef, lastVisitDate: Date): void {
        const unread =
            !this.isOwner(author) && this.originalComment.lastContentUpdatedDate
                ? lastVisitDate.getTime() < this.originalComment.lastContentUpdatedDate.getTime()
                : false;

        if (unread !== this.unread.value) {
            this.unread.value = unread;
        }
    }

    public setLike(like: boolean, identity: IdentityRef): void {
        const likeIndex = this.getLikeIndex(identity);
        if (like) {
            if (likeIndex < 0) {
                this.likes.push(identity);
            }
        } else {
            if (likeIndex >= 0) {
                this.likes.splice(likeIndex, 1);
            }
        }
    }

    public setSaving(saving: boolean): void {
        this.saving.value = saving;
    }

    public startEditing(): void {
        this.editing.value = true;
    }

    public updateComment(comment: IDiscussionComment): void {
        this.id = comment.id;
        this.originalComment = comment;

        this.checkDirty();
        this.editing.value = false;
        this.error.value = null;
    }

    private checkDirty(): void {
        const dirty = !stringEquals(this.originalComment.content, this.content.value);
        if (this.dirty.value !== dirty) {
            this.dirty.value = dirty;

            if (this.props.onDirtyChange) {
                this.props.onDirtyChange();
            }
        }
    }

    private getLikeIndex(identity: IdentityRef): number {
        return this.getLikes().value.findIndex(l => stringEquals(l.id, identity.id, true));
    }
}
